import { Element } from '../../equipment/models/elements/element';
import { HoleElement } from '../models/elements/holeElement';
import { Block } from '../models/block';
import { ProductGroup } from '../models/product-group';

type TranslateFn = (key: string, params?: Record<string, string | number>) => string;

const ELEMENT_I18N: Record<string, string> = {
	ShelfElement: 'SHELF',
	HangBarElement: 'HANG_BAR',
	HookElement: 'HOOK',
	HoleElement: 'PERFORATION',
	PalletElement: 'PALLET',
	BoxElement: 'BOX'
};

const countTypes = (elements: Array<Element>): Map<string, number> =>
	elements.reduce((counts, element) => {
		const typeName: string = element.constructor.name;
		counts.set(typeName, (counts.get(typeName) ?? 0) + 1);
		return counts;
	}, new Map<string, number>());
	
export function areBlockElementTypesEqual(source: Block, target: Block): boolean {
	if (!source.elements.length) return true;

	const sourceCounts: Map<string, number> = countTypes(source.elements);
	const targetCounts: Map<string, number> = countTypes(target.elements);

	if (sourceCounts.size !== targetCounts.size) return false;

	for (const [typeName, count] of sourceCounts) {
		if (targetCounts.get(typeName) !== count) return false;
	}

	return true;
}

const subtractCounts = (
	left: Map<string, number>,
	right: Map<string, number>
): Array<[string, number]> =>
	[...left.entries()]
		.map(([typeName, count]): [string, number] => [typeName, count - (right.get(typeName) ?? 0)])
		.filter(([, delta]) => delta > 0);

const formatCounts = (items: Array<[string, number]>, translate: TranslateFn): string =>
	items
		.map(([typeName, count]) => {
			const key: string | undefined = ELEMENT_I18N[typeName];
			const label: string = (key ? translate(key) : typeName.replace(/Element$/, '')).toLocaleLowerCase();
			return translate('BLOCK_ELEMENTS_ITEM', { name: label, count });
		})
		.join('; ');

export function buildBlockElementsMismatchDetails(
	source: Block,
	target: Block,
	translate: TranslateFn
): string {
	const sourceCounts: Map<string, number> = countTypes(source.elements);
	const targetCounts: Map<string, number> = countTypes(target.elements);
	const missing: Array<[string, number]> = subtractCounts(sourceCounts, targetCounts);
	const extra: Array<[string, number]> = subtractCounts(targetCounts, sourceCounts);

	const lines: Array<string> = [
		missing.length && translate('BLOCK_ELEMENTS_MISSING', { items: formatCounts(missing, translate) }),
		extra.length && translate('BLOCK_ELEMENTS_EXTRA', { items: formatCounts(extra, translate) })
	].filter(Boolean) as Array<string>;

	return lines.length ? `\n${lines.join('\n')}` : '';
}

export function getBlockProductPlacementErrorKey(
	element: Element | null,
	product: ProductGroup
): string | null {
	if (!element) return 'BLOCK_MOVE_NO_ELEMENT_ERROR';
	if (element.canDrop(product)) return null;

	if (product.isLooseForm) return 'WEIGHT_PRODUCT_ON_SHELF_ERROR';
	if (element instanceof HoleElement) return 'DROP_PRODUCT_ERROR_3';

	return 'BLOCK_MOVE_NO_ELEMENT_ERROR';
}
